import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, useWindowDimensions } from 'react-native';

import LightningBoltIcon from '@/assets/icons/lightning-bolt';
import WalletCashIcon from '@/assets/icons/wallet-cash';
import WalletGoldIcon from '@/assets/icons/wallet-gold';
import { Text } from '@/components/TextComponent';
import { Box, Column, Image, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { PackageRewardIcon } from '@/feature/account-details-sweeps/components/PackageRewardBadge';
import { CoinPackagesType } from '@/feature/account-details-sweeps/hooks/use-coin-packages';
import { getPackageRewards } from '@/feature/account-details-sweeps/utils/coin-package-rewards';
import { withOpacity } from '@/styles/styles';
import { toCoinsCurrency, toLocaleCurrency, toSocialCurrency } from '@/utils/numeric/currency';

const HEADER_ICON_SIZE = 24;
const REWARD_ICON_SIZE = 16;

type PurchaseSummaryCardProps = {
    coinPackage: CoinPackagesType;
};

export const PurchaseSummaryCard = ({ coinPackage }: PurchaseSummaryCardProps) => {
    const { t } = useTranslation(['common', 'wallet', 'sweeps']);
    const isFtd = coinPackage.is_ftd_only;
    const price = toLocaleCurrency(coinPackage.real_money_price);
    const hasCash = coinPackage.cash_amount > 0;
    const rewards = getPackageRewards(coinPackage.coin_package_id, t);
    const hasRewards = hasCash || rewards.length > 0;
    const hasMysteryGift = rewards.some(reward => reward.kind === 'mystery_gift');

    return (
        <Box width={'100%'} position={'relative'}>
            {isFtd ? <FirstTimePurchaseBanner /> : null}
            <Column
                mt={isFtd ? 's40' : 's0'}
                backgroundColor={ds.colorToken.background.neutral.subtler}
                borderColor={ds.colorToken.border.subtle}
                borderWidth={1}
                borderRadius={'r20'}
                padding="s16"
                gap="s20"
                testID="purchase-summary-card"
            >
                <Column gap="s16">
                    <Row gap="s16" alignItems="center">
                        <Row flex={1} gap="s8" alignItems="center">
                            {coinPackage.image_url ? (
                                <Image
                                    source={{ uri: coinPackage.image_url }}
                                    resizeMode="contain"
                                    style={styles.packageImage}
                                />
                            ) : (
                                <WalletGoldIcon size={HEADER_ICON_SIZE} />
                            )}
                            <Text variant="titleLarge2" fontWeight="500" numberOfLines={1} flexShrink={1}>
                                {toCoinsCurrency(coinPackage.coin_amount)} {t('sweeps:gold_coins')}
                            </Text>
                        </Row>
                        <Text variant="labelLarge2" fontWeight="600">
                            {price}
                        </Text>
                    </Row>
                    {hasRewards ? (
                        <Column gap="s12">
                            <Text variant="labelLarge2" color="neutral300">
                                {t('sweeps:additional_rewards')}
                            </Text>
                            {hasCash ? (
                                <RewardRow
                                    icon={<WalletCashIcon size={REWARD_ICON_SIZE} />}
                                    label={`${toSocialCurrency(coinPackage.cash_amount)} ${t('common:betr_cash')}`}
                                    trailing={t('common:free')}
                                />
                            ) : null}
                            {rewards.map(reward => (
                                <RewardRow
                                    key={reward.id}
                                    icon={<PackageRewardIcon kind={reward.kind} size={REWARD_ICON_SIZE} />}
                                    label={reward.label}
                                    trailing={t('common:free')}
                                />
                            ))}
                        </Column>
                    ) : null}
                    {hasMysteryGift ? (
                        <Text variant="bodyXSmall" color="neutral300">
                            {t('sweeps:mystery_gifts_note', { amount: toSocialCurrency(1000) })}
                        </Text>
                    ) : null}
                </Column>
                <Column gap="s16">
                    <Box height={1} backgroundColor={ds.colorToken.background.neutral.subtle} />
                    <Row justifyContent="space-between" alignItems="center" gap="s16">
                        <Text variant="titleLarge2">{t('common:total')}</Text>
                        <Text variant="titleLarge2">{price}</Text>
                    </Row>
                </Column>
            </Column>
        </Box>
    );
};

const RewardRow = ({ icon, label, trailing }: { icon: ReactNode; label: string; trailing: string }) => (
    <Row gap="s16" alignItems="center">
        <Row flex={1} gap="s6" alignItems="center">
            {icon}
            <Text variant="bodySmall2" numberOfLines={1} flexShrink={1}>
                {label}
            </Text>
        </Row>
        <Text variant="bodySmall2" color="neutral300">
            {trailing}
        </Text>
    </Row>
);

const FirstTimePurchaseBanner = () => {
    const { width } = useWindowDimensions();
    const horizontalPadding = 20;

    return (
        <Box alignItems={'center'}>
            <Box
                position={'absolute'}
                style={styles.ftdBanner}
                width={width - horizontalPadding * 2}
                height={70}
                pt={'s10'}
                borderRadius={'r16'}
            >
                <Row alignItems={'center'} justifyContent={'center'} gap={'s4'}>
                    <LightningBoltIcon color={ds.colorValue.icon.accent.amber} />
                    <Text variant={'labelLarge2'} color={ds.colorToken.text.accent.amber} textAlign={'center'}>
                        First time purchase boost applied
                    </Text>
                </Row>
            </Box>
        </Box>
    );
};

const styles = StyleSheet.create({
    packageImage: {
        width: HEADER_ICON_SIZE,
        height: HEADER_ICON_SIZE,
    },
    ftdBanner: {
        backgroundColor: withOpacity(ds.colorValue.background.accent.amber.strong, 0.15),
    },
});
